import _classNames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import ComplexIcon from '../icons/ComplexIcon/ComplexIcon';
import Icon from '../icons/Icon/Icon';

/**
 * A functional component that is a persistent sidebar menu component for the full page desktop dashboard application.
 */
function Sidebar({ children, footerActions, collapseSidebar, isCollapsed, logoIcon }) {
  return (
    <div className={_classNames('Sidebar', { 'Sidebar--collapsed': isCollapsed })}>
      <div className={_classNames('Sidebar__logo', { 'Sidebar__logo--collapsed': isCollapsed })}>
        {logoIcon ?? (
          <ComplexIcon
            name={isCollapsed ? 'logo-rainbow' : 'rainbow-logo-white-text'}
            classes={!isCollapsed && 'Sidebar__logo__text-icon'}
          />
        )}
      </div>
      {React.Children.map(
        children,
        (child) =>
          child &&
          React.cloneElement(child, { showtooltip: isCollapsed ? 'showtooltip' : undefined })
      )}
      <div className={_classNames('Sidebar__footer', { 'Sidebar--collapsed': isCollapsed })}>
        {React.Children.map(
          footerActions,
          (child) =>
            child &&
            React.cloneElement(child, { showtooltip: isCollapsed ? 'showtooltip' : undefined })
        )}
        <div
          className="Sidebar__item Sidebar__footer__toggle-collapse"
          onClick={collapseSidebar}
          onKeyPress={collapseSidebar}
          role="button"
          tabIndex={0}
        >
          <div className="Sidebar__item__icon-container">
            <Icon name="Chevron--double--west" size="minor" svgProps={{ fill: '#969fb1' }} />
          </div>
          {!isCollapsed ? (
            <span className="Sidebar__item__text Sidebar__footer__toggle-collapse-text">
              Collapse
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  /**
   * Create the list of options in the sidebar by including `SidebarItem` components as children
   */
  children: PropTypes.node,
  /**
   * Footer actions
   */
  footerActions: PropTypes.node,
  /**
   * A function that toggles the collapsed state of the side bar via a parent component.
   * This function gets added as the `onClick` handler to the Sidebar footer button.
   */
  collapseSidebar: PropTypes.func,
  /**
   * Set to `true` to render the collapsed version of the sidebar
   */
  isCollapsed: PropTypes.bool,
  /**
   * Optionally override the logo
   */
  logoIcon: PropTypes.node,
};

Sidebar.defaultProps = {
  collapseSidebar: () => {},
  isCollapsed: false,
  children: undefined,
  logoIcon: undefined,
};

export default Sidebar;
